import type { BrandedConnectorOptions } from '../branded';
import type { MysqlFamilyOptions } from '../mysql';
import { createBrandedConnector } from '../branded';
import mysql from '../mysql';

const brands: BrandedConnectorOptions<MysqlFamilyOptions>[] = [
	{
		id: 'planetscale-vitess',
		name: '$t:connectors.planetscale-vitess.name',
		description: '$t:connectors.planetscale-vitess.description',
		icon: 'simple-icons:planetscale',
		iconColor: '#000000',
		keywords: ['planetscale', 'planet scale', 'vitess', 'serverless mysql'],
		availableSince: '2026-06-16',
		supportLevel: 'beta',
		docsUrl: 'https://planetscale.com/docs/concepts/connection-strings',
		overrides: {
			connectionStringPlaceholder: 'mysql://user:password@host.connect.psdb.cloud:3306/dbname?ssl-mode=require',
		},
	},
	{
		id: 'tidb',
		name: '$t:connectors.tidb.name',
		description: '$t:connectors.tidb.description',
		icon: 'simple-icons:tidb',
		iconColor: '#DC150B',
		keywords: ['tidb', 'pingcap', 'tidb cloud', 'distributed sql', 'htap'],
		availableSince: '2026-06-16',
		supportLevel: 'beta',
		docsUrl: 'https://docs.pingcap.com/tidb/stable/dev-guide-connect-to-tidb/',
		overrides: {
			connectionStringPlaceholder: 'mysql://user:password@host:4000/dbname?ssl-mode=require',
			defaultPort: 4000,
		},
	},
	{
		id: 'singlestore',
		name: '$t:connectors.singlestore.name',
		description: '$t:connectors.singlestore.description',
		icon: 'simple-icons:singlestore',
		iconColor: '#AA00FF',
		keywords: ['singlestore', 'single store', 'memsql', 'mem sql'],
		availableSince: '2026-06-16',
		supportLevel: 'beta',
		docsUrl: 'https://docs.singlestore.com/cloud/connect-to-singlestore/connect-with-mysql/connect-with-any-mysql-compatible-tool-to-singlestore-helios/',
		overrides: {
			connectionStringPlaceholder: 'mysql://user:password@host.svc.singlestore.com:3306/dbname',
		},
	},
	{
		id: 'mysql-heatwave',
		name: '$t:connectors.mysql-heatwave.name',
		description: '$t:connectors.mysql-heatwave.description',
		icon: 'simple-icons:mysql',
		iconColor: '#4479A1',
		keywords: ['mysql heatwave', 'heatwave', 'oracle cloud mysql', 'oci mysql', 'oracle mysql'],
		availableSince: '2026-06-16',
		docsUrl: 'https://docs.oracle.com/en-us/iaas/mysql-database/doc/connecting-db-system.html',
		overrides: {
			connectionStringPlaceholder: 'mysql://user:password@<private-ip-or-fqdn>:3306/dbname',
		},
		notices: [
			{ color: 'neutral', title: '$t:connectors.mysql-heatwave.setup_notice' },
		],
	},
	{
		id: 'percona',
		name: '$t:connectors.percona.name',
		description: '$t:connectors.percona.description',
		icon: 'connectors:percona',
		iconColor: '#F0B336',
		keywords: ['percona', 'percona server', 'percona mysql'],
		availableSince: '2026-06-16',
		docsUrl: 'https://docs.percona.com/percona-server/8.4/',
		overrides: {
			connectionStringPlaceholder: 'mysql://user:password@host:3306/dbname',
		},
	},
	{
		id: 'aiven-mysql',
		name: '$t:connectors.aiven-mysql.name',
		description: '$t:connectors.aiven-mysql.description',
		icon: 'connectors:aiven',
		iconColor: '#FF5A00',
		keywords: ['aiven', 'aiven mysql', 'aiven managed mysql'],
		availableSince: '2026-06-16',
		docsUrl: 'https://aiven.io/docs/products/mysql/get-started',
		overrides: {
			connectionStringPlaceholder: 'mysql://avnadmin:password@service.aivencloud.com:12691/defaultdb?ssl-mode=REQUIRED',
		},
	},
	{
		id: 'digitalocean-mysql',
		name: '$t:connectors.digitalocean-mysql.name',
		description: '$t:connectors.digitalocean-mysql.description',
		icon: 'simple-icons:digitalocean',
		iconColor: '#0080FF',
		keywords: ['digitalocean', 'digital ocean', 'do mysql', 'managed mysql'],
		availableSince: '2026-06-16',
		docsUrl: 'https://docs.digitalocean.com/products/databases/mysql/how-to/connect/',
		overrides: {
			connectionStringPlaceholder: 'mysql://doadmin:password@my-cluster.db.ondigitalocean.com:25060/defaultdb?ssl-mode=require',
			defaultPort: 25_060,
		},
	},
	{
		id: 'railway-mysql',
		name: '$t:connectors.railway-mysql.name',
		description: '$t:connectors.railway-mysql.description',
		icon: 'simple-icons:railway',
		iconColor: '#0B0D0E',
		keywords: ['railway', 'railway mysql', 'railway.app mysql'],
		availableSince: '2026-06-16',
		docsUrl: 'https://docs.railway.com/databases/mysql',
		overrides: {
			connectionStringPlaceholder: 'mysql://root:password@mysql.railway.internal:3306/railway',
		},
		notices: [
			{ color: 'neutral', title: '$t:connectors.railway-mysql.setup_notice' },
		],
	},
	{
		id: 'aws-rds-mysql',
		name: '$t:connectors.aws-rds-mysql.name',
		description: '$t:connectors.aws-rds-mysql.description',
		icon: 'simple-icons:amazonrds',
		iconColor: '#527FFF',
		keywords: ['aws', 'amazon', 'rds', 'mysql', 'relational database service', 'cloud'],
		availableSince: '2026-06-16',
		docsUrl: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/USER_ConnectToInstance.html',
		overrides: {
			connectionStringPlaceholder: 'mysql://admin:password@mydb.123456789012.us-east-1.rds.amazonaws.com:3306/dbname',
		},
	},
	{
		id: 'aws-aurora-mysql',
		name: '$t:connectors.aws-aurora-mysql.name',
		description: '$t:connectors.aws-aurora-mysql.description',
		icon: 'connectors:aurora',
		iconColor: '#527FFF',
		keywords: ['aws', 'amazon', 'aurora', 'mysql', 'aurora mysql', 'cloud'],
		availableSince: '2026-06-16',
		docsUrl: 'https://docs.aws.amazon.com/AmazonRDS/latest/AuroraUserGuide/Aurora.Connecting.html',
		overrides: {
			connectionStringPlaceholder: 'mysql://admin:password@mycluster.cluster-123456789012.us-east-1.rds.amazonaws.com:3306/dbname',
		},
	},
	{
		id: 'gcp-cloud-sql-mysql',
		name: '$t:connectors.gcp-cloud-sql-mysql.name',
		description: '$t:connectors.gcp-cloud-sql-mysql.description',
		icon: 'simple-icons:googlecloud',
		iconColor: '#4285F4',
		keywords: ['gcp', 'google', 'cloud sql', 'mysql', 'google cloud'],
		availableSince: '2026-06-16',
		docsUrl: 'https://docs.cloud.google.com/sql/docs/mysql/connect-overview',
		overrides: {
			connectionStringPlaceholder: 'mysql://root:password@203.0.113.2:3306/mydb?ssl-mode=REQUIRED',
		},
		notices: [
			{ color: 'neutral', title: '$t:connectors.gcp-cloud-sql-mysql.setup_notice' },
		],
	},
	{
		id: 'azure-mysql',
		name: '$t:connectors.azure-mysql.name',
		description: '$t:connectors.azure-mysql.description',
		icon: 'connectors:azure',
		iconColor: '#0078D4',
		keywords: ['azure', 'microsoft', 'mysql', 'flexible server'],
		availableSince: '2026-06-16',
		docsUrl: 'https://learn.microsoft.com/en-us/azure/mysql/flexible-server/connect-python',
		overrides: {
			connectionStringPlaceholder: 'mysql://myadmin:password@mydemoserver.mysql.database.azure.com:3306/mydatabase?ssl-mode=REQUIRED',
		},
		notices: [
			{ color: 'neutral', title: '$t:connectors.azure-mysql.setup_notice' },
		],
	},
];

export const mysqlPresets = brands.map((brand) => createBrandedConnector(mysql, brand));
